// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState, useMemo } from 'react';
import Modal from './Modal';
import Button from './Button';
import { useCargo } from '../contexts/CargoContext';
import { useCredits } from '../contexts/CreditsContext';
import { useModules } from '../contexts/ModulesContext';
import tradeGoods from '../config/TradeGoodsConfig';
import { calculatePrice } from '../utils/game/TradeUtils';
import MarketInventoryManager from '../utils/game/MarketInventoryManager';
import './TradeShop.css';

const TradeShop = ({ isOpen, onClose, location }) => {
  const { getCargoQuantity, getCargoUsed, addCargo, removeCargo } = useCargo();
  const { credits, addCredits, removeCredits } = useCredits();
  const { getCargoCapacity } = useModules();
  
  const [buyQuantities, setBuyQuantities] = useState({});
  const [sellQuantities, setSellQuantities] = useState({});
  const [feedback, setFeedback] = useState(null);
  const [activeTab, setActiveTab] = useState('buy'); // 'buy' or 'sell'

  const maxCapacity = getCargoCapacity();
  const cargoUsed = getCargoUsed(tradeGoods);
  const cargoRemaining = maxCapacity - cargoUsed;

  // Generate location ID for inventory tracking
  const locationId = location.id || location.name || 'unknown';

  // Get market inventory for this location
  const marketInventory = useMemo(() => {
    return MarketInventoryManager.getLocationInventory(locationId, location);
  }, [locationId, location]);

  // Calculate prices for all goods (memoized)
  const goodsPrices = useMemo(() => {
    return Object.values(tradeGoods).map(good => {
      const stock = marketInventory[good.id];
      return {
        ...good,
        buyPrice: calculatePrice(good.id, location, true, locationId),
        sellPrice: calculatePrice(good.id, location, false, locationId),
        owned: getCargoQuantity(good.id),
        stock: stock?.current || 0,
        maxStock: stock?.max || 0,
        stockPercent: stock ? Math.round((stock.current / stock.max) * 100) : 0
      };
    });
  }, [location, locationId, marketInventory, getCargoQuantity]);

  // Calculate totals for buy/sell
  const buyTotal = useMemo(() => {
    return Object.entries(buyQuantities).reduce((sum, [goodId, qty]) => {
      if (qty > 0) {
        const good = goodsPrices.find(g => g.id === goodId);
        return sum + (good?.buyPrice || 0) * qty;
      }
      return sum;
    }, 0);
  }, [buyQuantities, goodsPrices]);

  const sellTotal = useMemo(() => {
    return Object.entries(sellQuantities).reduce((sum, [goodId, qty]) => {
      if (qty > 0) {
        const good = goodsPrices.find(g => g.id === goodId);
        return sum + (good?.sellPrice || 0) * qty;
      }
      return sum;
    }, 0);
  }, [sellQuantities, goodsPrices]);

  const buyCargoNeeded = useMemo(() => {
    return Object.entries(buyQuantities).reduce((sum, [goodId, qty]) => {
      if (qty > 0) {
        const good = tradeGoods[goodId];
        return sum + (good?.weight || 0) * qty;
      }
      return sum;
    }, 0);
  }, [buyQuantities]);

  // No cargo bay installed
  if (maxCapacity === 0) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="📦 TRADE GOODS"
        size="medium"
        className="trade-shop-no-cargo"
      >
        <div className="no-cargo-bay">
          <div className="no-cargo-icon">🚫</div>
          <div className="no-cargo-text">
            <h3>NO CARGO BAY INSTALLED</h3>
            <p>You need to install a Cargo Bay module to trade goods.</p>
            <p>Visit the Ship Modules shop at any station.</p>
          </div>
        </div>
      </Modal>
    );
  }

  const adjustQuantity = (goodId, delta, isBuy) => {
    if (isBuy) {
      setBuyQuantities(prev => {
        const current = prev[goodId] || 0;
        const newQty = Math.max(0, current + delta);
        const good = goodsPrices.find(g => g.id === goodId);
        // Limit to available stock
        const maxQty = Math.min(newQty, good?.stock || 0);
        return { ...prev, [goodId]: maxQty };
      });
    } else {
      setSellQuantities(prev => {
        const current = prev[goodId] || 0;
        const newQty = Math.max(0, current + delta);
        const good = goodsPrices.find(g => g.id === goodId);
        // Limit to owned quantity
        const maxQty = Math.min(newQty, good?.owned || 0);
        return { ...prev, [goodId]: maxQty };
      });
    }
  };

  const setQuantity = (goodId, value, isBuy) => {
    const qty = Math.max(0, parseInt(value) || 0);
    if (isBuy) {
      setBuyQuantities(prev => {
        const good = goodsPrices.find(g => g.id === goodId);
        const maxQty = Math.min(qty, good?.stock || 0);
        return { ...prev, [goodId]: maxQty };
      });
    } else {
      setSellQuantities(prev => {
        const good = goodsPrices.find(g => g.id === goodId);
        const maxQty = Math.min(qty, good?.owned || 0);
        return { ...prev, [goodId]: maxQty };
      });
    }
  };

  const executeBuyAll = () => {
    let totalCost = 0;
    let totalWeight = 0;
    const transactions = [];

    // Validate all transactions first
    for (const [goodId, qty] of Object.entries(buyQuantities)) {
      if (qty > 0) {
        const good = tradeGoods[goodId];
        const goodData = goodsPrices.find(g => g.id === goodId);
        const cost = goodData.buyPrice * qty;
        const weight = good.weight * qty;

        if (goodData.stock < qty) {
          showFeedback(`Insufficient stock for ${good.name}!`, 'error');
          return;
        }

        totalCost += cost;
        totalWeight += weight;
        transactions.push({ goodId, qty, cost, good });
      }
    }

    if (transactions.length === 0) {
      showFeedback('No items selected to buy!', 'error');
      return;
    }

    if (credits < totalCost) {
      showFeedback(`Insufficient credits! Need ${totalCost.toLocaleString()} CR.`, 'error');
      return;
    }

    if (totalWeight > cargoRemaining) {
      showFeedback(`Insufficient cargo space! Need ${totalWeight} units.`, 'error');
      return;
    }

    // Execute all transactions
    removeCredits(totalCost, `bought ${transactions.length} items`);
    transactions.forEach(({ goodId, qty, good }) => {
      addCargo(goodId, qty);
      MarketInventoryManager.updateStock(locationId, goodId, -qty);
      MarketInventoryManager.recordTrade(goodId, qty); // Track demand
    });

    showFeedback(`✓ Purchased ${transactions.length} items for ${totalCost.toLocaleString()} CR`, 'success');
    setBuyQuantities({});
  };

  const executeSellAll = () => {
    let totalRevenue = 0;
    const transactions = [];

    // Validate all transactions first
    for (const [goodId, qty] of Object.entries(sellQuantities)) {
      if (qty > 0) {
        const good = tradeGoods[goodId];
        const goodData = goodsPrices.find(g => g.id === goodId);
        const revenue = goodData.sellPrice * qty;

        if (goodData.owned < qty) {
          showFeedback(`You don't have enough ${good.name}!`, 'error');
          return;
        }

        totalRevenue += revenue;
        transactions.push({ goodId, qty, revenue, good });
      }
    }

    if (transactions.length === 0) {
      showFeedback('No items selected to sell!', 'error');
      return;
    }

    // Execute all transactions
    addCredits(totalRevenue, `sold ${transactions.length} items`);
    transactions.forEach(({ goodId, qty }) => {
      removeCargo(goodId, qty);
      MarketInventoryManager.updateStock(locationId, goodId, qty);
      MarketInventoryManager.recordTrade(goodId, -qty); // Track oversupply
    });

    showFeedback(`✓ Sold ${transactions.length} items for ${totalRevenue.toLocaleString()} CR`, 'success');
    setSellQuantities({});
  };

  const showFeedback = (message, type) => {
    setFeedback({ message, type });
    setTimeout(() => setFeedback(null), 3000);
  };

  const clearAll = () => {
    if (activeTab === 'buy') {
      setBuyQuantities({});
    } else {
      setSellQuantities({});
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="📦 TRADE GOODS"
      size="large"
      className="trade-shop-modal"
    >
      {/* Transaction Summary at Top */}
      <div className="trade-summary-header">
        <div className="summary-section">
          <div className="summary-label">YOUR CREDITS</div>
          <div className="summary-value credits">{credits.toLocaleString()} CR</div>
        </div>
        
        {activeTab === 'buy' ? (
          <>
            <div className="summary-section">
              <div className="summary-label">TOTAL COST</div>
              <div className={`summary-value ${buyTotal > credits ? 'error' : 'buy'}`}>
                {buyTotal.toLocaleString()} CR
              </div>
            </div>
            <div className="summary-section">
              <div className="summary-label">CARGO NEEDED</div>
              <div className={`summary-value ${buyCargoNeeded > cargoRemaining ? 'error' : ''}`}>
                {buyCargoNeeded} / {cargoRemaining}
              </div>
            </div>
            <div className="summary-section">
              <div className="summary-label">AFTER PURCHASE</div>
              <div className={`summary-value ${credits - buyTotal < 0 ? 'error' : ''}`}>
                {(credits - buyTotal).toLocaleString()} CR
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="summary-section">
              <div className="summary-label">TOTAL REVENUE</div>
              <div className="summary-value sell">{sellTotal.toLocaleString()} CR</div>
            </div>
            <div className="summary-section">
              <div className="summary-label">CARGO FREED</div>
              <div className="summary-value">
                {Object.entries(sellQuantities).reduce((sum, [goodId, qty]) => {
                  const good = tradeGoods[goodId];
                  return sum + (good?.weight || 0) * qty;
                }, 0)} units
              </div>
            </div>
            <div className="summary-section">
              <div className="summary-label">AFTER SALE</div>
              <div className="summary-value sell">{(credits + sellTotal).toLocaleString()} CR</div>
            </div>
          </>
        )}
      </div>

      {/* Location Info */}
      <div className="trade-shop-info">
        <div className="info-item">
          <span className="info-label">Location:</span>
          <span className="info-value">{location.name}</span>
        </div>
        <div className="info-item">
          <span className="info-label">Cargo:</span>
          <span className="info-value">{cargoUsed}/{maxCapacity} units</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="trade-tabs">
        <Button
          variant={activeTab === 'buy' ? 'primary' : 'ghost'}
          size="medium"
          onClick={() => setActiveTab('buy')}
        >
          BUY GOODS
        </Button>
        <Button
          variant={activeTab === 'sell' ? 'primary' : 'ghost'}
          size="medium"
          onClick={() => setActiveTab('sell')}
        >
          SELL GOODS
        </Button>
      </div>

      {/* Feedback */}
      {feedback && (
        <div className={`trade-feedback ${feedback.type}`}>
          {feedback.message}
        </div>
      )}

      {/* Goods List with +/- Controls */}
      <div className="trade-goods-list">
        {goodsPrices.map(good => {
          const isBuy = activeTab === 'buy';
          const quantity = isBuy ? (buyQuantities[good.id] || 0) : (sellQuantities[good.id] || 0);
          const price = isBuy ? good.buyPrice : good.sellPrice;
          const canTrade = isBuy 
            ? good.stock > 0
            : good.owned > 0;

          // Stock status for buy mode
          let stockStatus = '';
          let stockClass = '';
          if (isBuy) {
            if (good.stock === 0) {
              stockStatus = 'OUT OF STOCK';
              stockClass = 'out-of-stock';
            } else if (good.stockPercent <= 25) {
              stockStatus = 'LOW STOCK';
              stockClass = 'low-stock';
            }
          }

          return (
            <div
              key={good.id}
              className={`trade-good-row ${!canTrade ? 'disabled' : ''} ${quantity > 0 ? 'selected' : ''}`}
            >
              <div className="good-icon">{good.icon}</div>
              
              <div className="good-info">
                <div className="good-name">{good.name}</div>
                <div className="good-stats">
                  <span className="stat">Weight: {good.weight}</span>
                  <span className="stat">Price: {price} CR</span>
                  {isBuy && (
                    <span className={`stat stock ${stockClass}`}>
                      Stock: {good.stock}
                      {stockStatus && ` (${stockStatus})`}
                    </span>
                  )}
                  {!isBuy && good.owned > 0 && (
                    <span className="stat owned">Owned: {good.owned}</span>
                  )}
                </div>
              </div>

              <div className="quantity-controls">
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => adjustQuantity(good.id, -10, isBuy)}
                  disabled={!canTrade || quantity === 0}
                >
                  -10
                </Button>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => adjustQuantity(good.id, -1, isBuy)}
                  disabled={!canTrade || quantity === 0}
                >
                  -1
                </Button>
                <input
                  type="number"
                  className="quantity-input"
                  value={quantity}
                  onChange={(e) => setQuantity(good.id, e.target.value, isBuy)}
                  min="0"
                  max={isBuy ? good.stock : good.owned}
                  disabled={!canTrade}
                />
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => adjustQuantity(good.id, 1, isBuy)}
                  disabled={!canTrade || (isBuy ? quantity >= good.stock : quantity >= good.owned)}
                >
                  +1
                </Button>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => adjustQuantity(good.id, 10, isBuy)}
                  disabled={!canTrade || (isBuy ? quantity >= good.stock : quantity >= good.owned)}
                >
                  +10
                </Button>
              </div>

              <div className="good-total">
                {quantity > 0 && (
                  <>
                    <div className="total-label">{quantity}x</div>
                    <div className="total-value">{(price * quantity).toLocaleString()} CR</div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Action Buttons */}
      <div className="trade-actions">
        <Button
          variant="ghost"
          size="medium"
          onClick={clearAll}
        >
          CLEAR ALL
        </Button>
        <Button
          variant={activeTab === 'buy' ? 'success' : 'warning'}
          size="large"
          onClick={activeTab === 'buy' ? executeBuyAll : executeSellAll}
          disabled={activeTab === 'buy' ? buyTotal === 0 : sellTotal === 0}
        >
          {activeTab === 'buy' 
            ? `BUY ALL (${buyTotal.toLocaleString()} CR)` 
            : `SELL ALL (${sellTotal.toLocaleString()} CR)`}
        </Button>
      </div>
    </Modal>
  );
};

export default TradeShop;
